import React, {useEffect, useState} from "react";
import {TrainingRecordList, TrainingRecordRestControllerApiFactory, TrainingReportDto, TrainingSessionDto} from "arteco-api-client-ts";
import AppList, {AppListConfig} from "../../../../components/layout/list";
import {
	Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Stack,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import {useApiManager} from "../../../../components/utils/api";
import {COLOR_DISMISSED} from "../../../../components/layout/colors";
import {dateToString} from "../../../../components/utils/dates";
import {FilterChips, Panel, PersonAvatar, SoftChip, tableContainerSx, tableSx} from "../../../../components/kit";
import {TRAINING_KIND_LABELS, TRAINING_STATUS} from "./labels";

const HOY = () => new Date().toISOString().substring(0, 10);

/** Formación y concienciación por persona (#13719): fichas, informe del año y sesiones de grupo. */
export const TrainingRecordListPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(TrainingRecordRestControllerApiFactory);
	const currentYear = new Date().getFullYear();
	const [year, setYear] = useState<number>(currentYear);
	const [trigger, setTrigger] = useState(0);
	const [report, setReport] = useState<TrainingReportDto>();
	const [sesion, setSesion] = useState<TrainingSessionDto | null>(null);

	useEffect(() => {
		apiManager.execute(ctrl.reportTrainingRecord(year)).then(setReport);
	}, [year, trigger]);

	const registrarSesion = () => {
		if (!sesion) return;
		apiManager.execute(ctrl.registerSessionTrainingRecord(sesion), "Sesión registrada").then(() => {
			setSesion(null);
			setTrigger(trigger + 1);
		});
	};

	const listConfig: AppListConfig<TrainingRecordList> = {
		title: "Formación y concienciación",
		onClick: "/app/admin/employee/training",
		headers: [
			{title: "Fecha"},
			{title: "Persona"},
			{title: "Tipo"},
			{title: "Tema"},
			{title: "Horas", align: "right"},
			{title: "Estado"},
		],
		decorator: (bean: TrainingRecordList) => (
			<>
				<TableCell style={{color: bean.dismissed ? COLOR_DISMISSED : ""}} sx={{whiteSpace: "nowrap"}}>
					{dateToString(bean.trainingDate)}
				</TableCell>
				<TableCell>{(bean.person as any)?.surnamesAndName ?? "—"}</TableCell>
				<TableCell>{TRAINING_KIND_LABELS[bean.kind || ""] || bean.kind}</TableCell>
				<TableCell>{bean.topic}</TableCell>
				<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{bean.hours ?? "—"}</TableCell>
				<TableCell>
					{bean.status && <SoftChip tone={TRAINING_STATUS[bean.status]?.tone || "neutral"}
											  label={TRAINING_STATUS[bean.status]?.label || bean.status}/>}
				</TableCell>
			</>
		),
		supplier: async (currentPage, size, term, showDismissed) => {
			// Posicionales: personId, year, kind, status, term, showDismissed.
			return ctrl.pageTrainingRecord(currentPage, size, undefined, undefined, year, undefined, undefined, term, showDismissed);
		},
		toolbar: (
			<Stack direction={{xs: "column", sm: "row"}} spacing={1.5} alignItems={{sm: "center"}} justifyContent="space-between" sx={{mb: 2}}>
				<FilterChips value={year} onChange={(y) => { setYear(y); setTrigger(trigger + 1); }}
							 options={[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map(y => ({value: y, label: String(y)}))}/>
				<Button variant="outlined" startIcon={<GroupsRoundedIcon/>} sx={{alignSelf: {xs: "flex-start", sm: "auto"}}}
						onClick={() => setSesion({
							kind: "CONCIENCIACION", topic: "", trainingDate: HOY(), hours: 1, status: "REALIZADA",
						} as TrainingSessionDto)}>
					Sesión para toda la plantilla
				</Button>
			</Stack>
		),
	};

	return (
		<>
			<AppList config={listConfig} trigger={trigger}/>

			{report && (
				<Panel sx={{mt: 3}} title={`Por persona en ${report.year}`}
					   subtitle={(report.withoutAwareness
						   ? `${report.withoutAwareness} de ${(report.rows || []).length} personas sin ninguna sesión de concienciación este año.`
						   : "Toda la plantilla tiene al menos una sesión de concienciación este año.")
						   + ((report.withoutPassedCheck || 0) > (report.withoutAwareness || 0)
							   ? ` ${(report.withoutPassedCheck || 0) - (report.withoutAwareness || 0)} la hicieron sin comprobación superada o sin registrarla.`
							   : "")}
					   action={<SoftChip tone={report.withoutAwareness ? "warning" : "success"}
										 label={report.withoutAwareness ? `${report.withoutAwareness} sin concienciación` : "Concienciación al día"}/>}>
					<TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
						<Table size="small" sx={{...tableSx, minWidth: 660}}>
							<TableHead>
								<TableRow>
									<TableCell>Persona</TableCell><TableCell align="right">Horas</TableCell>
									<TableCell align="right">Realizadas</TableCell><TableCell align="right">Planificadas</TableCell>
									<TableCell>Concienciación</TableCell><TableCell>Comprobación</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{(report.rows || []).map(r => (
									<TableRow key={r.personId} hover>
										<TableCell>
											<Stack direction="row" spacing={1.5} alignItems="center">
												<PersonAvatar name={r.name} size={28}/>
												<Typography variant="body2" fontWeight={600}>{r.name}</Typography>
											</Stack>
										</TableCell>
										<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{r.hours}</TableCell>
										<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{r.done}</TableCell>
										<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{r.planned}</TableCell>
										<TableCell>
											<SoftChip tone={r.awareness ? "success" : "warning"} label={r.awareness ? "Sí" : "Falta"}/>
										</TableCell>
										<TableCell>
											{r.awareness && <SoftChip tone={r.awarenessPassed ? "success" : "warning"}
																	  label={r.awarenessPassed ? "Superada" : "Sin superar"}/>}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Panel>
			)}

			<Dialog open={!!sesion} onClose={() => setSesion(null)} maxWidth="sm" fullWidth PaperProps={{sx: {borderRadius: 3}}}>
				<DialogTitle sx={{fontWeight: 700}}>Sesión para toda la plantilla</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
						Crea una ficha por cada persona de alta en la fecha de la sesión.
					</Typography>
					{sesion && (
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<TextField select fullWidth size="small" label="Tipo" value={sesion.kind}
										   onChange={(e) => setSesion({...sesion, kind: e.target.value as any})}>
									<MenuItem value="CONCIENCIACION">Concienciación</MenuItem>
									<MenuItem value="FORMACION">Formación</MenuItem>
								</TextField>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField select fullWidth size="small" label="Estado" value={sesion.status}
										   onChange={(e) => setSesion({...sesion, status: e.target.value as any})}>
									<MenuItem value="REALIZADA">Realizada</MenuItem>
									<MenuItem value="PLANIFICADA">Planificada</MenuItem>
								</TextField>
							</Grid>
							<Grid item xs={12}>
								<TextField fullWidth size="small" label="Tema" value={sesion.topic}
										   onChange={(e) => setSesion({...sesion, topic: e.target.value})}/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField fullWidth size="small" type="date" label="Fecha" value={sesion.trainingDate}
										   InputLabelProps={{shrink: true}}
										   onChange={(e) => setSesion({...sesion, trainingDate: e.target.value})}/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField fullWidth size="small" type="number" label="Horas" value={sesion.hours ?? ""}
										   onChange={(e) => setSesion({...sesion, hours: e.target.value ? Number(e.target.value) : undefined})}/>
							</Grid>
							<Grid item xs={12}>
								<TextField fullWidth size="small" label="Impartida por" value={sesion.provider ?? ""}
										   onChange={(e) => setSesion({...sesion, provider: e.target.value})}/>
							</Grid>
						</Grid>
					)}
				</DialogContent>
				<DialogActions sx={{px: 3, pb: 2.5}}>
					<Button onClick={() => setSesion(null)}>Cancelar</Button>
					<Button variant="contained" disabled={!sesion?.topic} onClick={registrarSesion}>Registrar</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
